import { useMutation } from "@apollo/client/react";
import cx from "classnames";

import CodeEditor from "components/CodeEditor";
import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Callout from "ds/components/Callout";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Select from "ds/components/Select";
import useTypedContext from "hooks/useTypedContext";
import { trackSegmentEvent } from "shared/Analytics";
import { Policy, PolicyEngineType } from "types/generated";
import { withTestId } from "utils/withTestId";

import { showLoginPolicyDetailsConfirmationModal } from "./ConfirmationModal";
import {
  LOGIN_POLICY_UPDATE_ACTIVE_SESSIONS_WARNING_STORAGE_KEY,
  LOGIN_POLICY_UPDATE_BODY_WRAPPER_TEST_ID,
} from "./constants";
import { UPDATE_POLICY_V2 } from "../gql";
import styles from "../styles.module.css";

type LoginPolicyDetailsBodyContentProps = {
  policy: Policy;
  body: string;
  handleEditorChange: (value?: string) => void;
  isSimulationVisible: boolean;
  isLoginPolicyActive: boolean;
  policyEngineType: PolicyEngineType;
  setPolicyEngineType: (value: PolicyEngineType) => void;
};

const LoginPolicyDetailsBodyContent = ({
  body,
  handleEditorChange,
  isSimulationVisible,
  policy,
  isLoginPolicyActive,
  policyEngineType,
  setPolicyEngineType,
}: LoginPolicyDetailsBodyContentProps) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const [updatePolicyV2, { loading }] = useMutation(UPDATE_POLICY_V2, {
    variables: {
      id: policy.id,
      input: {
        name: policy.name,
        space: policy.space,
        labels: policy.labels,
        description: policy.description,
        body,
        engineType: policyEngineType,
      },
    },
    refetchQueries: ["GetPolicyDetails"],
    onCompleted: () => {
      trackSegmentEvent("Login Policy Body Updated");
      reportSuccess({ message: `Login policy successfully updated` });
    },
  });

  const handleSaveChanges = async () => {
    try {
      await updatePolicyV2();
    } catch (error) {
      onError(error);
    }
  };

  const handleConfirmSave = () => {
    showLoginPolicyDetailsConfirmationModal({ onConfirm: handleSaveChanges });
  };

  return (
    <Box
      {...withTestId(LOGIN_POLICY_UPDATE_BODY_WRAPPER_TEST_ID)}
      direction="column"
      className={cx(styles.bodyWrapper, isSimulationVisible && styles.narrowed)}
    >
      <PageInfoHeader title="Policy body">
        <Select
          value={policyEngineType}
          items={[
            { label: "Rego V0", value: PolicyEngineType.RegoV0 },
            { label: "Rego V1", value: PolicyEngineType.RegoV1 },
          ]}
          onChange={(value: PolicyEngineType) => setPolicyEngineType(value)}
        />
        <Button
          variant="primary"
          onPress={isLoginPolicyActive ? handleConfirmSave : handleSaveChanges}
          loading={loading}
        >
          Save changes
        </Button>
      </PageInfoHeader>

      {isLoginPolicyActive && (
        <Callout
          variant="danger"
          storageKey={LOGIN_POLICY_UPDATE_ACTIVE_SESSIONS_WARNING_STORAGE_KEY}
        >
          After changing the Login policy, all active sessions (except the current one) will be
          invalidated.
        </Callout>
      )}
      <CodeEditor body={body} onChange={handleEditorChange} language="rego" />
    </Box>
  );
};

export default LoginPolicyDetailsBodyContent;
